import {
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  OnInit,
  output,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { ContactSubmissionsFirebaseService } from '@core/services/firebase/contact-submissions-firebase.service';
import { environment } from '@environments/environment';
import { Store } from '@ngxs/store';
import { LogoutUser } from '@states/auth/auth.actions';
import { AuthSelectors } from '@states/auth/auth.selectors';
import { CommunicationsSelectors } from '@states/communications/communications.selectors';
import { ConversationsSelectors } from '@states/conversations';
import * as ConversationsActions from '@states/conversations/conversations.actions';
import { OrganizationState } from '@states/organization/organization.state';
import { LucideDynamicIcon, LucideIconInput } from '@lucide/angular';
import {
  Bell,
  Calendar,
  Compass,
  Heart,
  House,
  LayoutDashboard,
  LogOut,
  Mail2,
  MailIcon,
  MapPin,
  Menu,
  MessageCircle,
  Plus,
  Settings,
  Shield,
  Truck,
  User,
  UsersIcon,
} from '@core/icons/lucide.icons';
import { MessagesDrawerComponent } from '../messages-drawer/messages-drawer.component';
import { NotificationsDrawerComponent } from '../notifications-drawer/notifications-drawer.component';

interface MenuOption {
  route?: string; // Optional route for navigation
  label: string;
  icon?: LucideIconInput;
  action?: string;
  class?: string;
  hostOnly?: boolean; // Only show if user has an organization
  guestOnly?: boolean; // Only show if user does NOT have an organization
}

type UserMenuOption = MenuOption;

@Component({
  selector: 'app-navbar',
  imports: [
    LucideDynamicIcon,
    RouterLink,
    RouterLinkActive,
    MessagesDrawerComponent,
    NotificationsDrawerComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './navbar.html',
})
export class NavbarComponent implements OnInit, OnDestroy {
  private store = inject(Store);
  private contactService = inject(ContactSubmissionsFirebaseService);

  // Outputs
  navigate = output<string>();

  // State from NGXS
  isLoggedIn = this.store.selectSignal(AuthSelectors.isAuthenticated);
  currentUser = this.store.selectSignal(AuthSelectors.getUser);
  userOrganizations = this.store.selectSignal(OrganizationState.userOrganizations);

  // Computed
  isDevelopment = !environment.production;

  isAdmin = computed(() => this.currentUser()?.role === 'admin');
  // Check if user owns an organization (has organizationId field)
  hasOrganization = computed(() => !!this.currentUser()?.organizationId);

  // Contact messages count
  newContactMessagesCount = signal(0);

  constructor() {
    // Subscribe to conversations whenever user changes
    effect(() => {
      const user = this.currentUser();
      if (user?.id) {
        // Subscribe to real-time conversations for this user
        this.store.dispatch(new ConversationsActions.SubscribeToConversations(user.id));
      } else {
        // Unsubscribe when user logs out
        this.store.dispatch(new ConversationsActions.UnsubscribeFromConversations());
      }
    });
  }

  ngOnInit(): void {
    // Initial subscription if user is already logged in
    const userId = this.currentUser()?.id;
    if (userId) {
      this.store.dispatch(new ConversationsActions.SubscribeToConversations(userId));
    }
  }

  ngOnDestroy(): void {
    // Clean up subscription when component is destroyed
    this.store.dispatch(new ConversationsActions.UnsubscribeFromConversations());
  }

  // Local state
  messagesOpen = this.store.selectSignal(ConversationsSelectors.messagesDrawerOpen);
  notificationsOpen = signal(false);
  userMenuOpen = signal(false);

  // Unread counts - use signal-based selectors
  private allConversations = this.store.selectSignal(ConversationsSelectors.conversations);

  unreadCount = computed(() => {
    const userId = this.currentUser()?.id;
    const conversations = this.allConversations();

    if (!userId || !conversations) return 0;

    return conversations.reduce((total, conv) => {
      return total + (conv.unreadCount?.[userId] || 0);
    }, 0);
  });

  unreadNotificationsCount = computed(() =>
    this.store.selectSignal(CommunicationsSelectors.unreadNotificationsCount)(),
  );

  // Lucide icons for template
  HomeIcon = House;
  HeartIcon = Heart;
  MessageCircleIcon = MessageCircle;
  BellIcon = Bell;
  MenuIcon = Menu;
  UserIcon = User;
  PlusIcon = Plus;
  Mail2Icon = Mail2;

  // Filter menu options based on user role and organization
  filteredMenuOptions = computed(() => {
    const isUserAdmin = this.isAdmin();
    const isHost = this.hasOrganization();
    return this.userMenuOptions.filter((option) => {
      // Hide admin items from non-admins
      if (option.class?.includes('admin-only') && !isUserAdmin) {
        return false;
      }
      // Hide host-only items from non-hosts
      if (option.hostOnly && !isHost) {
        return false;
      }
      // Hide guest-only items from hosts
      if (option.guestOnly && isHost) {
        return false;
      }
      return true;
    });
  });

  // User menu options array
  userMenuOptions: UserMenuOption[] = [
    {
      label: 'Explore',
      icon: Compass,
      action: 'navigate',
      route: 'search',
      class: 'md:hidden', // Only show on mobile
    },
    {
      label: 'Destinations',
      icon: MapPin,
      action: 'navigate',
      route: 'destinations',
      class: 'md:hidden', // Only show on mobile
    },
    {
      label: 'Separator',
      class: 'md:hidden', // Only show separator on mobile
    },
    {
      label: 'Favorites',
      icon: Heart,
      action: 'navigate',
      route: 'favorites',
      class: 'md:hidden', // Only show on mobile
    },
    {
      label: 'Messages',
      icon: MessageCircle,
      action: 'toggleMessages',
      class: 'md:hidden', // Only show on mobile
    },
    {
      label: 'Separator',
      class: 'md:hidden', // Only show separator on mobile
    },
    {
      label: 'Profile',
      icon: User,
      action: 'navigate',
      route: 'profile',
    },
    {
      label: 'My Trips',
      icon: Calendar,
      action: 'navigate',
      route: 'trips',
    },
    {
      label: 'Settings',
      icon: Settings,
      action: 'navigate',
      route: 'profile/settings',
    },
    {
      label: 'Separator',
      hostOnly: true,
    },
    {
      label: 'Host Dashboard',
      icon: LayoutDashboard,
      action: 'navigate',
      route: 'host',
      hostOnly: true,
    },
    {
      label: 'Vehicles',
      icon: Truck,
      action: 'navigate',
      route: 'host/vehicles',
      hostOnly: true,
    },
    {
      label: 'Bookings',
      icon: Calendar,
      action: 'navigate',
      route: 'host/bookings',
      hostOnly: true,
    },
    // {
    //   label: 'Team',
    //   icon: Users,
    //   action: 'navigate',
    //   route: 'host/team',
    //   hostOnly: true,
    // },
    {
      label: 'Calendar',
      icon: Calendar,
      action: 'navigate',
      route: 'host/calendar',
      hostOnly: true,
    },
    // {
    //   label: 'Payouts',
    //   icon: CreditCard,
    //   action: 'navigate',
    //   route: 'host/payouts',
    //   hostOnly: true,
    // },
    // {
    //   label: 'Separator',
    //   guestOnly: true,
    // },
    // {
    //   label: 'Become a Host',
    //   icon: Plus,
    //   action: 'navigate',
    //   route: 'become-a-host',
    //   class: 'text-indigo-600 font-medium',
    //   guestOnly: true, // Only show if user does NOT have an organization
    // },
    {
      label: 'Separator',
      class: 'admin-only',
    },
    {
      label: 'Admin Dashboard',
      icon: Shield,
      action: 'navigate',
      route: 'admin',
      class: 'admin-only text-red-600 font-semibold',
    },
    {
      label: 'Users',
      icon: UsersIcon,
      action: 'navigate',
      route: 'admin/users',
      class: 'admin-only',
    },
    {
      label: 'Inbox',
      icon: MailIcon,
      action: 'navigate',
      route: 'admin/inbox',
      class: 'admin-only',
    },
    // {
    //   label: 'Host Verification',
    //   icon: Shield,
    //   action: 'navigate',
    //   route: 'admin/host-verification',
    //   class: 'admin-only',
    // },
    // {
    //   label: 'Financials',
    //   icon: DollarSign,
    //   action: 'navigate',
    //   route: 'admin/financial-tracking',
    //   class: 'admin-only',
    // },
    // {
    //   label: 'Payouts',
    //   icon: Handshake,
    //   action: 'navigate',
    //   route: 'admin/payouts',
    //   class: 'admin-only',
    // },
    // {
    //   label: 'Functions',
    //   icon: SquareFunction,
    //   action: 'navigate',
    //   route: 'admin/function-test',
    //   class: 'admin-only',
    // },
    // {
    //   label: 'SendGrid',
    //   icon: Mail,
    //   action: 'navigate',
    //   route: 'admin/sendgrid',
    //   class: 'admin-only',
    // },
    {
      label: 'Separator',
    },
    {
      label: 'Sign out',
      icon: LogOut,
      action: 'signOut',
      class: 'text-red-600 hover:bg-red-50',
    },
  ];

  onNavigate(page: string): void {
    this.navigate.emit(page);
  }

  onSignIn(): void {
    // Navigate to sign-in page
    this.navigate.emit('sign-in');
  }

  toggleMessages(): void {
    this.store.dispatch(new ConversationsActions.OpenMessagesDrawer(!this.messagesOpen()));
  }

  toggleNotifications(): void {
    this.notificationsOpen.set(!this.notificationsOpen());
  }

  setNotificationsOpen(open: boolean): void {
    this.notificationsOpen.set(open);
  }

  navigateToCommunications(): void {
    this.navigate.emit('communications');
  }

  navigateToMessages(): void {
    this.navigate.emit('communications?tab=messages');
  }

  setMessagesOpen(open: boolean): void {
    this.store.dispatch(new ConversationsActions.OpenMessagesDrawer(open));
  }

  toggleUserMenu(): void {
    this.userMenuOpen.set(!this.userMenuOpen());
  }

  closeUserMenu(): void {
    this.userMenuOpen.set(false);
  }

  async signOut(): Promise<void> {
    try {
      await this.store.dispatch(new LogoutUser()).toPromise();
      this.closeUserMenu();
    } catch (err) {
      console.error('Sign out error:', err);
    }
  }

  onMenuItemClick(option: MenuOption): void {
    // Don't process separators
    if (option.label === 'Separator' || !option.action) {
      return;
    }

    if (option.action === 'signOut') {
      this.signOut();
    } else if (option.action === 'toggleMessages') {
      this.toggleMessages();
      this.closeUserMenu();
    } else if (option.action === 'navigate' && option.route) {
      this.onNavigate(option.route);
      this.closeUserMenu();
    } else if (option.action) {
      // Fallback to old action-based navigation
      this.onNavigate(option.action);
      this.closeUserMenu();
    }
  }

  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    const dropdownContainer = target.closest('.relative');

    // If click is outside the dropdown container, close the menu
    if (!dropdownContainer && this.userMenuOpen()) {
      this.closeUserMenu();
    }
  }
}
